"use client";

import * as DialogPrimitive from "@radix-ui/react-dialog";
import { Button } from "@/app/components/ui/button";
import { useConsentStore } from "@/app/stores/consent-store";
import {
  acceptAllConsentAction,
  rejectAllConsentAction,
  readConsentAction,
} from "@/app/services/server/actions/consent";
import * as Sentry from "@sentry/nextjs";
import { updateSentryBasedOnConsent } from "@/instrumentation-client";
import { useEffect, useState } from "react";

export default function CookieNoticeDialog() {
  const { isBannerOpen, setBannerOpen, setCustomizeOpen, setConsent } =
    useConsentStore();
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const stored = await readConsentAction();
        setConsent(stored);
        setBannerOpen(!stored.decided);
      } catch (error) {
        // Network aborts (e.g. Safari "Load failed") must not surface as unhandled rejections
        console.error("Failed to read consent state", error);
        Sentry.captureException(error, { tags: { flow: "consent" } });
        setBannerOpen(true);
      }
    })();
  }, [setConsent, setBannerOpen]);

  const onAccept = async () => {
    setIsSaving(true);
    try {
      const next = await acceptAllConsentAction();
      setConsent(next);
      updateSentryBasedOnConsent(next);
      setBannerOpen(false);
    } catch (error) {
      console.error("Failed to save consent", error);
      Sentry.captureException(error, { tags: { flow: "consent" } });
    } finally {
      setIsSaving(false);
    }
  };

  const onReject = async () => {
    setIsSaving(true);
    try {
      const next = await rejectAllConsentAction();
      setConsent(next);
      updateSentryBasedOnConsent(next);
      setBannerOpen(false);
    } catch (error) {
      console.error("Failed to save consent", error);
      Sentry.captureException(error, { tags: { flow: "consent" } });
    } finally {
      setIsSaving(false);
    }
  };

  const handleOpenChange = (open: boolean) => {
    // Only allow closing through explicit actions, not by clicking outside
    if (!open) {
      // Don't close on outside click
      return;
    }
    setBannerOpen(open);
  };

  return (
    <DialogPrimitive.Root
      open={isBannerOpen}
      onOpenChange={handleOpenChange}
      modal={false}
    >
      <DialogPrimitive.Portal>
        <div className="hidden">
          <DialogPrimitive.Title>Cookie Einstellungen</DialogPrimitive.Title>
        </div>
        <DialogPrimitive.Content className="bg-background data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=open]:fade-in-0 data-[state=closed]:fade-out-0 data-[state=open]:slide-in-from-top-4 data-[state=closed]:slide-out-to-top-2 fixed right-4 bottom-6 z-50 w-[min(420px,calc(100%-2rem))] translate-x-0 translate-y-0 rounded-lg border p-5 shadow-2xl duration-200 focus:outline-none sm:right-6">
          <div className="mb-2">
            <h2 className="text-lg font-semibold">Cookie Einstellungen</h2>
          </div>
          <p className="text-muted-foreground text-sm">
            Wir verwenden Cookies und ähnliche Technologien, um Inhalte zu
            personalisieren, Werbung anzupassen und zu messen, sowie ein
            besseres Nutzererlebnis zu bieten.
          </p>
          <div className="mt-5 flex flex-col justify-start gap-3 sm:flex-row sm:items-center">
            <div className="grid w-full gap-2 max-sm:flex-row-reverse sm:flex">
              <Button
                className="w-full sm:w-auto"
                variant="default"
                onClick={onAccept}
                disabled={isSaving}
              >
                Alle akzeptieren
              </Button>
              <Button
                className="w-full sm:w-auto"
                variant="secondary"
                onClick={onReject}
                disabled={isSaving}
              >
                Ablehnen
              </Button>
            </div>
            <Button
              className="order-first self-end max-sm:p-0 max-sm:pb-3 sm:order-last"
              variant="link"
              onClick={() => {
                setBannerOpen(false);
                setCustomizeOpen(true);
              }}
            >
              Auswählen
            </Button>
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
